import React, { useState, useEffect, useRef } from "react";
import Post from "./Post";
import PostBox from "./PostBox";
import "./Feed.css";
import { 
  Flame, 
  Sparkles, 
  RefreshCw, 
  AlertCircle, 
  Inbox, 
  Puzzle,
  ChevronDown,
  Loader2,
} from "lucide-react";

export default function Feed({ posts, loading, error, refreshPosts }) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = localStorage.getItem("token");
  const [sort, setSort] = useState("recent");
  const [isScrolling, setIsScrolling] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const scrollTimeout = useRef(null);
  const feedRef = useRef(null);

  useEffect(() => {
    setInitialLoad(false);
  }, []);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolling(true);
          clearTimeout(scrollTimeout.current);
          scrollTimeout.current = setTimeout(() => {
            setIsScrolling(false);
          }, 150);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const changeSort = (value) => {
    setSort(value);
    refreshPosts(value);
  };

  const scrollToTop = () => {
    feedRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main 
      ref={feedRef}
      className={`feed-minimal ${isScrolling ? 'scrolling' : ''}`}
    >

      {/* Minimal Header */}
      <div className="feed-header-minimal">
        <div className="header-title">
          <h2>Feed</h2>
          <span className="post-count">{posts.length} posts</span>
        </div>
        
        <div className="header-actions">
          {/* Sort Dropdown */}
          <div className="sort-dropdown">
            <button 
              className="sort-trigger"
              onClick={() => document.getElementById('sort-select').click()}
            >
              {sort === 'recent' ? <Flame size={16} /> : <Sparkles size={16} />}
              <span>{sort === 'recent' ? 'Newest' : 'Top'}</span>
              <ChevronDown size={14} />
            </button>
            <select 
              id="sort-select"
              value={sort} 
              onChange={(e) => changeSort(e.target.value)}
              className="sort-select"
            >
              <option value="recent">Newest</option>
              <option value="top">Most Reacted</option>
            </select>
          </div>

          {/* Refresh Button */}
          <button 
            className="icon-button"
            onClick={() => refreshPosts(sort)}
            aria-label="Refresh feed"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Composer - Only when logged in */}
      {token && (
        <div className="composer-minimal">
          <div className="composer-collapsed">
            <div className="composer-user">
              <span className="composer-name">
                {user?.name}
              </span>
            </div>
            <button
              className={`compose-trigger-btn ${isComposerOpen ? "active" : ""}`}
              onClick={() => setIsComposerOpen(prev => !prev)} aria-label="Toggle composer">
              <Puzzle size={18} />
            </button>
          </div>
          <div className={`composer-expanded ${isComposerOpen ? "open" : ""}`}>
            <PostBox
              minimal
              refreshPosts={() => {
                refreshPosts(sort);
                setIsComposerOpen(false);
              }}
            />
          </div>
        </div>
      )}
      {/* Loading State */}
      {loading && (
        <div className="feed-state">
          <Loader2 size={32} className="spin" />
          <p>Loading conversations...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="feed-state error">
          <AlertCircle size={32} />
          <p>Failed to load feed</p>
          <button 
            className="retry-button"
            onClick={() => refreshPosts(sort)}
          >
            <RefreshCw size={14} />
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && posts.length === 0 && (
        <div className="feed-state">
          <Inbox size={32} />
          <h4>No posts yet</h4>
          <p>Be the first to start a conversation</p>
        </div>
      )}

      {/* Posts Stream */}
      {!loading && posts.length > 0 && (
        <div className="posts-stream">
          {posts.map((post, i) => (
            <div 
              key={post._id || i}
              className={`post-card-wrapper ${initialLoad ? 'animate' : ''}`}
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <Post
                post={post}
                compact
                refreshPosts={() => refreshPosts(sort)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Scroll to Top Button - Shows after scrolling */}
      {isScrolling && (
        <button 
          className="scroll-top-button"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <ChevronDown size={20} />
        </button>
      )}
    </main>
  );
}